import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ShieldCheck, Copy, Check, Plus, Trash2, Scale, Briefcase, EyeOff, Route, Send,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { api, formatApiError } from '@/lib/api';

const SERVICE_TYPES = [
  'legal_advisory', 'independent_investigation', 'audit_control',
  'training_prevention', 'advisor_contact',
];

const SR_STATUS_CLS = {
  new: 'bg-sky-50 text-sky-700 border-sky-200',
  contacted: 'bg-amber-50 text-amber-700 border-amber-200',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STEP_ICONS = [Send, Route, Scale];

export default function ClientGlobalEthics() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [srType, setSrType] = useState('');
  const [srNote, setSrNote] = useState('');
  const [srBusy, setSrBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        api.get('/client/ethics/config'),
        api.get('/client/ethics/service-requests'),
      ]);
      setCfg(c.data);
      setRows(c.data?.authorized_recipients || []);
      setMine(m.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(cfg.access_code); } catch { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const validRows = rows.every((r) => (r.name || '').trim().length >= 2
    && (r.title || '').trim().length >= 2 && (r.email || '').includes('@'));

  const saveRecipients = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/client/ethics/recipients', { recipients: rows });
      setRows(data.authorized_recipients);
      toast.success(t('globalEthicsClient.recipients.saved'));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendServiceRequest = async () => {
    setSrBusy(true);
    try {
      await api.post('/client/ethics/service-requests', { type: srType, note: srNote || null });
      toast.success(t('globalEthicsClient.services.sent'));
      setSrType(''); setSrNote('');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSrBusy(false); }
  };

  const servicesCard = (
    <Card className="p-6 lg:p-8" data-testid="ge-client-services-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Briefcase className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">{t('globalEthicsClient.services.title')}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mt-1">
            {t('globalEthicsClient.services.hint')}
          </p>
        </div>
      </div>
      <div className="mt-6 grid gap-6 lg:gap-10 lg:grid-cols-[1.25fr_1fr] items-start">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">{t('globalEthicsAdmin.tabs.services')}</Label>
            <Select value={srType} onValueChange={setSrType}>
              <SelectTrigger className="w-full text-base" data-testid="ge-client-service-type-select">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`ge-client-service-type-${s}`}>
                    {t(`globalEthicsAdmin.services.types.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">{t('globalEthicsClient.services.noteLabel')}</Label>
            <Textarea rows={5} className="min-h-[140px] w-full md:text-base" value={srNote}
                      onChange={(e) => setSrNote(e.target.value)}
                      placeholder={t('globalEthicsClient.services.notePlaceholder')}
                      data-testid="ge-client-service-note-input" />
          </div>
          <Button className="rounded-full" disabled={!srType || srBusy}
                  onClick={sendServiceRequest} data-testid="ge-client-service-send-btn">
            {t('globalEthicsClient.services.send')}
          </Button>
        </div>
        <div className="rounded-xl border bg-muted/30 p-5 space-y-3" data-testid="ge-client-my-requests">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {t('globalEthicsClient.services.mineTitle')}
          </p>
          {mine.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="ge-client-my-requests-empty">
              {t('globalEthicsClient.services.mineEmpty')}
            </p>
          ) : mine.map((s) => (
            <div key={s.id} className="flex items-center gap-3 text-base rounded-lg border bg-white px-3.5 py-2.5">
              <span>{t(`globalEthicsAdmin.services.types.${s.type}`)}</span>
              <Badge variant="outline" className={`text-sm ml-auto ${SR_STATUS_CLS[s.status] || ''}`}>
                {t(`globalEthicsAdmin.services.statuses.${s.status}`)}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  const howCard = (
    <Card className="p-6 space-y-5 lg:col-span-2" data-testid="ge-client-how-card">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Scale className="h-5 w-5 text-primary" /> {t('globalEthicsClient.how.title')}
      </h2>
      <ol className="space-y-5">
        {['p1', 'p2', 'p3'].map((k, i) => {
          const Icon = STEP_ICONS[i];
          return (
            <li key={k} className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <p className="text-base text-muted-foreground leading-relaxed">
                {t(`globalEthicsClient.how.${k}`)}
              </p>
            </li>
          );
        })}
      </ol>
    </Card>
  );

  return (
    <div data-testid="client-global-ethics-page">
      <Topbar title={t('globalEthicsClient.title')} />
      <div className="p-6 lg:p-8 max-w-[1440px] mx-auto space-y-6">
        {/* Intro */}
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center shrink-0">
            <ShieldCheck className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('globalEthicsClient.title')}</h1>
            <p className="text-base text-muted-foreground leading-relaxed mt-0.5">
              {t('globalEthicsClient.subtitle')}
            </p>
          </div>
        </div>

        {cfg && !cfg.configured && (
          <Card className="px-6 py-5 flex items-center gap-4" data-testid="ge-client-not-configured">
            <div className="h-11 w-11 rounded-full bg-muted flex items-center justify-center shrink-0">
              <EyeOff className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('globalEthicsClient.notConfiguredTitle')}</h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                {t('globalEthicsClient.notConfigured')}
              </p>
            </div>
          </Card>
        )}

        {cfg?.configured ? (
          <>
            {/* Row 1 — access information + supporting "how it works" */}
            <div className="grid gap-6 lg:grid-cols-5 items-start">
              <Card className="p-6 space-y-4 lg:col-span-3" data-testid="ge-client-code-card">
                  <h2 className="text-lg font-semibold">{t('globalEthicsClient.codeCard.title')}</h2>
                  {!cfg.active && (
                    <p className="text-sm rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-3"
                       data-testid="ge-client-inactive-note">
                      {t('globalEthicsClient.inactive')}
                    </p>
                  )}
                  <div className="rounded-xl border bg-muted/30 p-5 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-mono text-2xl font-semibold tracking-[0.2em]" data-testid="ge-client-access-code">
                        {cfg.access_code}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t('globalEthicsClient.codeCard.urlLabel')}:{' '}
                        <span className="font-medium text-foreground text-base">{window.location.origin}/ethics</span>
                      </p>
                    </div>
                    <Button variant="outline" className="rounded-full gap-2" onClick={copyCode}
                            data-testid="ge-client-copy-code-btn">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? t('globalEthicsClient.codeCard.copied') : t('globalEthicsClient.codeCard.copy')}
                    </Button>
                  </div>
                <p className="text-base text-muted-foreground">{t('globalEthicsClient.codeCard.hint')}</p>
              </Card>
              {howCard}
            </div>

            {/* Row 2 — authorized recipients, full width */}
            <Card className="p-6 lg:p-8 space-y-4" data-testid="ge-client-recipients-card">
              <div>
                <h2 className="text-lg font-semibold">{t('globalEthicsClient.recipients.title')}</h2>
                <p className="text-base text-muted-foreground mt-1 leading-relaxed">
                  {t('globalEthicsClient.recipients.hint')}
                </p>
              </div>
              {rows.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="ge-client-recipients-empty">
                  {t('globalEthicsClient.recipients.empty')}
                </p>
              )}
              {rows.map((r, i) => (
                <div key={r.id || i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-center">
                  <Input value={r.name || ''} className="md:text-base" onChange={(e) => upd(i, 'name', e.target.value)}
                         placeholder={t('globalEthicsClient.recipients.name')} data-testid={`ge-client-recip-name-${i}`} />
                  <Input value={r.title || ''} className="md:text-base" onChange={(e) => upd(i, 'title', e.target.value)}
                         placeholder={t('globalEthicsClient.recipients.role')} data-testid={`ge-client-recip-title-${i}`} />
                  <Input value={r.email || ''} className="md:text-base" onChange={(e) => upd(i, 'email', e.target.value)}
                         placeholder={t('globalEthicsClient.recipients.email')} data-testid={`ge-client-recip-email-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                          data-testid={`ge-client-recip-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" className="rounded-full gap-2"
                        onClick={() => setRows((r) => [...r, { name: '', title: '', email: '' }])}
                        data-testid="ge-client-recip-add-btn">
                  <Plus className="h-4 w-4" /> {t('globalEthicsClient.recipients.add')}
                </Button>
                <Button className="rounded-full" disabled={!validRows || saving}
                        onClick={saveRecipients} data-testid="ge-client-recip-save-btn">
                  {t('globalEthicsClient.recipients.save')}
                </Button>
              </div>
            </Card>

            {/* Row 3 — CTH assistance, full width */}
            {servicesCard}
          </>
        ) : (
          <div className="grid gap-6 lg:grid-cols-5 items-start">
            <div className="lg:col-span-3">{servicesCard}</div>
            {howCard}
          </div>
        )}

        {/* Legal notice — full width, readable */}
        <Card className="p-6 lg:p-8" data-testid="ge-client-disclaimer-card">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <Scale className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{t('globalEthics.disclaimer.title')}</h2>
            <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
              {t('globalEthics.disclaimer.pendingTag')}
            </span>
          </div>
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground max-w-4xl">
            {['p1', 'p2', 'p4', 'p5'].map((k) => <p key={k}>{t(`globalEthics.disclaimer.${k}`)}</p>)}
          </div>
        </Card>
      </div>
    </div>
  );
}
